import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import useAuth from 'src/hooks/useAuth';
// Actions
import actionEditarAgendamento from 'src/actions/agendamentos/actionEditarAgendamento';
import actionListaAgendamentos from 'src/actions/agendamentos/actionListaCampanhas';
import v2_actionCriarAgendamento from 'src/actions/v2/agendamentos/v2_actionCriarAgendamento';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v1_actionWboTemplates from 'src/actions/v1/meta/wbo/v1_actionWboTemplates';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import { getListaOptionsFunil } from 'src/redux/slices/global';
// Components
import { MENSAGENS_AGENDADAS_DESIGN } from 'src/pages/chats/MensagensAgendadas.design';
import { cn } from 'src/@/lib/utils';
import { fDateTimeSuffix } from 'src/utils/formatTime';
import MicRecorder from 'mic-recorder-to-mp3';
import { PopoverVariaveis } from 'src/sections/@dashboard/automacoes/components/PopoverVariaveis';
// UI
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Switch } from 'src/@/components/ui/switch';
import { toast } from 'src/@/components/ui/use-toast';
import { Label } from 'src/@/components/ui/label';
import { Sheet, SheetContent, SheetTrigger } from 'src/@/components/ui/sheet';
import { Tooltip } from '@mui/material';
// Icons
import Iconify from 'src/components/Iconify';
import { BsMicFill } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa';

// Wrapped Component ( The Form )
function DrawerAgendamentosContent({ numeroDefault, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { cliente, user } = useAuth();

    const STEPS = [
        { id: 1, label: t('chat.scheduling.steps.recipient.label'), subtitle: t('chat.scheduling.steps.recipient.subtitle') },
        { id: 2, label: t('chat.scheduling.steps.content.label'), subtitle: t('chat.scheduling.steps.content.subtitle') },
        { id: 3, label: t('chat.scheduling.steps.schedule.label'), subtitle: t('chat.scheduling.steps.schedule.subtitle') },
    ];
    // Config
    const [listaClientesCanais, setListaClientesCanais] = useState([]);
    const isMultisession = cliente.multiSession === 1;
    // Wizard / Steps
    const [activeStep, setActiveStep] = useState(1);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    // Form Fields
    const [numero, setNumero] = useState(numeroDefault || '');
    const [selectedClienteId, setSelectedClienteId] = useState(
        isMultisession ? cliente.cliente_id_padrao || cliente._id : cliente._id
    );
    const [isChatAtendimento, setIsChatAtendimento] = useState(false);
    // Content State
    const [contentType, setContentType] = useState('text'); // 'text','file','audio','template'
    const [body, setBody] = useState('');
    // Files & Audio
    const [files, setFiles] = useState([]);
    const [audioGravado, setAudioGravado] = useState(null);
    const [recording, setRecording] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);
    // Templates
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateSelecionado, setTemplateSelecionado] = useState(null);
    const [templateVariaveis, setTemplateVariaveis] = useState({});
    const [useTemplate, setUseTemplate] = useState(false);
    // Schedule
    const [dataAgendamento, setDataAgendamento] = useState(new Date().toISOString().split('T')[0]);
    const [hora, setHora] = useState('');
    const [minuto, setMinuto] = useState('');

    // Derived
    const isWhatsAppBusiness = useMemo(() => {
        const channel = listaClientesCanais.find(
            (c) => c.cliente_id === selectedClienteId || c.clienteId === selectedClienteId
        );
        return channel?.platform === 'whatsapp_business';
    }, [selectedClienteId, listaClientesCanais]);

    // --- Effects ---
    useEffect(() => {
        dispatch(getListaOptionsFunil(user.clienteId));
        if (isMultisession) {
            buscarListaClientesCanais();
        }
    }, []);

    useEffect(() => {
        // Fetch templates if channel changes and is WABA
        const fetchTemplates = async () => {
            if (isWhatsAppBusiness && selectedClienteId) {
                setLoadingTemplates(true);
                try {
                    const res = await v1_actionWboTemplates(selectedClienteId);
                    setTemplates(res.data?.data?.templates || []);
                } catch (error) {
                    console.log('Erro ao buscar templates', error);
                } finally {
                    setLoadingTemplates(false);
                }
            }
        };
        fetchTemplates();
    }, [isWhatsAppBusiness, selectedClienteId]);

    // --- Actions ---
    const buscarListaClientesCanais = async () => {
        try {
            const res = await actionListaCanaisPorCliente(cliente._id);
            setListaClientesCanais(res.data.data);
            if (res.data.data.length > 0 && !selectedClienteId) {
                setSelectedClienteId(res.data.data[0].cliente_id || res.data.data[0].clienteId);
            }
        } catch (err) {
            console.log(err);
        }
    };

    // --- Helper Functions ---
    const normalizarComponents = (components) => {
        if (!Array.isArray(components)) return [];
        return components.map((c) => ({
            ...c,
            type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
        }));
    };

    const extrairVariaveisTemplate = (components) => {
        const variaveis = {};
        if (!components || !Array.isArray(components)) return variaveis;
        components.forEach((component) => {
            if (component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach((match) => {
                        const numero = match.replace(/\{\{|\}\}/g, '');
                        variaveis[numero] = '';
                    });
                }
            }
        });
        return variaveis;
    };

    const handleSelecionarTemplate = (templateId) => {
        const template = templates.find((t) => (t.templateId || t._id) === templateId);
        if (!template) {
            setTemplateSelecionado(null);
            return;
        }
        const comps = normalizarComponents(template?.components || []);
        const conteudo = comps?.find((c) => c.type === 'BODY')?.text || '';
        const variaveis = extrairVariaveisTemplate(comps);
        setTemplateSelecionado({
            templateId: template.templateId || template._id,
            name: template.name || template.templateName,
            conteudo: conteudo,
            components: comps,
        });
        setTemplateVariaveis(variaveis);
    };

    const handleStartRecording = async () => {
        setLoadingAudio(true);
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            await recorder.start();
            setRecording(true);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleStopRecording = async () => {
        try {
            const [buffer, blob] = await recorder.stop().getMp3();
            const file = new File(buffer, 'audio.mp3', { type: blob.type, lastModified: Date.now() });
            setAudioGravado(file);
            setRecording(false);
        } catch (error) {
            console.log(error);
        }
    };

    const uploadFiles = async () => {
        const uploaded = [];
        // Normal Files
        for (let file of files) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const res = await v2_actionUploadS3(formData, `?path=plataforma/agendamentos`);
                if (!res.data.error) {
                    uploaded.push({ key: res.data.data.key, type: file.type });
                }
            } catch (e) {
                console.log(e);
            }
        }
        // Audio
        if (audioGravado) {
            const formData = new FormData();
            formData.append('file', audioGravado);
            try {
                const res = await v2_actionUploadS3(formData, `?path=plataforma/agendamentos`);
                if (!res.data.error) {
                    uploaded.push({ key: res.data.data.key, type: audioGravado.type, tipo: 'audio' });
                }
            } catch (e) {
                console.log(e);
            }
        }
        return uploaded;
    };

    // --- Nav Handlers ---
    const handleNext = () => {
        if (activeStep === 1) {
            if (!numero) return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.missingNumber'), variant: 'destructive' });
            if (isMultisession && !selectedClienteId)
                return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.selectChannel'), variant: 'destructive' });
        }
        if (activeStep === 2) {
            if (contentType === 'text' && !body && !useTemplate)
                return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.messageOrTemplate'), variant: 'destructive' });
            if (useTemplate && !templateSelecionado)
                return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.selectTemplate'), variant: 'destructive' });
            if (contentType === 'file' && files.length === 0 && contentType === 'audio' && !audioGravado && !useTemplate)
                return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.attachFileOrAudio'), variant: 'destructive' });
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const onSubmit = async () => {
        if (!hora || !minuto) return toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.errors.missingTime'), variant: 'destructive' });
        setLoadingSubmit(true);
        try {
            let uploadedFiles = [];
            if (contentType === 'file' || contentType === 'audio') {
                uploadedFiles = await uploadFiles();
            }
            const payload = {
                dataAgendamento,
                hora,
                minuto,
                numero: numero.replace(/\D/g, ''), // Clean number
                texto: contentType === 'text' ? body : '', // Map fields based on contentType
                isFile: contentType === 'file' || contentType === 'audio',
                files: uploadedFiles,
                isChatAtendimento,
                // Template fields
                useTemplate: useTemplate,
                templateSelecionado: useTemplate ? templateSelecionado : null,
                templateVariaveis: useTemplate ? templateVariaveis : null,
                // Legacy/Generic fields for consistency
                scope: {
                    phone: numero,
                    dateSchedule: dataAgendamento,
                    hour: hora,
                    minute: minuto,
                    status_envio: 'agendado',
                    cliente_id: selectedClienteId,
                    files: uploadedFiles,
                    useTemplate: useTemplate,
                    templateSelecionado: useTemplate ? templateSelecionado : null,
                    templateVariaveis: useTemplate ? templateVariaveis : null,
                    isChatAtendimento: isChatAtendimento,
                },
            };
            await v2_actionCriarAgendamento(selectedClienteId, payload);
            toast({ title: t('notifications.toast.success'), description: t('chat.scheduling.success') });
            onClose();
        } catch (error) {
            console.log(error);
            toast({ title: t('notifications.toast.error'), description: t('chat.scheduling.saveError'), variant: 'destructive' });
        } finally {
            setLoadingSubmit(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
                <h2 className="text-xl font-bold text-gray-900">{t('chat.scheduling.new')}</h2>
            </div>
            {/* Stepper */}
            <div className="bg-white border-b border-gray-100 py-6 px-10">
                <div className="relative">
                    <div className="absolute top-[14px] left-0 w-full h-[2px] bg-gray-100 -z-0" />
                    <div
                        className="absolute top-[14px] left-0 h-[2px] bg-[#7C3AED] transition-all duration-500 ease-in-out -z-0"
                        style={{ width: `${((activeStep - 1) / (STEPS.length - 1)) * 100}%` }}
                    />
                    <div className="flex justify-between relative z-10">
                        {STEPS.map((step) => {
                            const isActive = step.id === activeStep;
                            const isCompleted = step.id < activeStep;
                            return (
                                <div key={step.id} className="flex flex-col items-center gap-2 bg-white px-2">
                                    <div
                                        className={cn(
                                            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all border-2',
                                            isActive
                                                ? 'bg-[#7C3AED] border-[#7C3AED] text-white'
                                                : isCompleted
                                                    ? 'bg-green-500 border-green-500 text-white'
                                                    : 'bg-white border-gray-200 text-gray-300'
                                        )}
                                    >
                                        {isCompleted ? <FaCheck size={10} /> : step.id}
                                    </div>
                                    <span className={cn('text-[10px] font-semibold uppercase tracking-wider', isActive ? 'text-[#7C3AED]' : 'text-gray-400')}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 scroll-smooth">
                {activeStep === 1 && (
                    <div className="space-y-6">
                        <div>
                            <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>{t('chat.scheduling.phoneNumber')}</Label>
                            <Input
                                placeholder={t('chat.scheduling.phoneNumberPlaceholder')}
                                value={numero}
                                onChange={(e) => setNumero(e.target.value)}
                                className="h-12 text-lg"
                            />
                            <p className="text-xs text-gray-400 mt-1">{t('chat.scheduling.phoneNumberHelp')}</p>
                        </div>
                        {isMultisession && (
                            <div>
                                <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>{t('chat.scheduling.sendChannel')}</Label>
                                <Select value={selectedClienteId} onValueChange={setSelectedClienteId}>
                                    <SelectTrigger className="h-10">
                                        <SelectValue placeholder={t('chat.scheduling.selectChannel')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {listaClientesCanais.map((c) => {
                                            const val = c.cliente_id || c.clienteId;
                                            return (
                                                <SelectItem key={val} value={val}>
                                                    {c.nome} ({c.platform})
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <div>
                                <p className="text-sm font-medium">{t('chat.scheduling.serviceChat')}</p>
                                <p className="text-xs text-gray-500">{t('chat.scheduling.serviceChatHelp')}</p>
                            </div>
                            <Switch checked={isChatAtendimento} onCheckedChange={setIsChatAtendimento} />
                        </div>
                    </div>
                )}
                {activeStep === 2 && (
                    <div className="space-y-6">
                        <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>{t('chat.scheduling.messageType')}</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {['text', 'file', 'audio'].map((type) => (
                                <div
                                    key={type}
                                    onClick={() => setContentType(type)}
                                    className={cn(
                                        'cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all',
                                        contentType === type
                                            ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED]'
                                            : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                                    )}
                                >
                                    <Iconify
                                        icon={
                                            type === 'text'
                                                ? 'mdi:format-text'
                                                : type === 'file'
                                                    ? 'mdi:file-document-outline'
                                                    : 'mdi:microphone-outline'
                                        }
                                        width={24}
                                    />
                                    <span className="text-xs font-semibold">{type === 'text' ? t('chat.scheduling.contentTypes.text') : type === 'file' ? t('chat.scheduling.contentTypes.file') : t('chat.scheduling.contentTypes.audio')}</span>
                                </div>
                            ))}
                        </div>
                        {isWhatsAppBusiness && (
                            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
                                <div>
                                    <p className="text-sm font-medium text-green-900">{t('chat.scheduling.useTemplate')}</p>
                                    <p className="text-xs text-green-700">{t('chat.scheduling.useTemplateHelp')}</p>
                                </div>
                                <Switch checked={useTemplate} onCheckedChange={setUseTemplate} />
                            </div>
                        )}
                        <div className="pt-2">
                            {contentType === 'text' && (
                                <div className="space-y-2">
                                    <div className="flex justify-end">
                                        <PopoverVariaveis />
                                    </div>
                                    <Textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder={t('chat.scheduling.messagePlaceholder')}
                                        className="h-40 resize-none text-base bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                                    />
                                </div>
                            )}
                            {contentType === 'file' && (
                                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors">
                                    <Input type="file" multiple className="hidden" id="file" onChange={(e) => setFiles(Array.from(e.target.files))} />
                                    <label htmlFor="file" className="cursor-pointer flex flex-col items-center gap-2">
                                        <Iconify icon="mdi:cloud-upload-outline" width={40} className="text-gray-300" />
                                        <span className="text-sm text-gray-600 font-medium">{t('chat.scheduling.clickToSelectFiles')}</span>
                                        {files.length > 0 && <span className="text-sm text-[#7C3AED]">{t('chat.scheduling.filesSelected', { count: files.length })}</span>}
                                    </label>
                                </div>
                            )}
                            {contentType === 'audio' && (
                                <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200 gap-4">
                                    {!audioGravado ? (
                                        !recording ? (
                                            <Button
                                                variant="outline"
                                                onClick={handleStartRecording}
                                                className="h-12 px-8 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                            >
                                                <BsMicFill className="mr-2" /> {t('chat.scheduling.recordAudio')}
                                            </Button>
                                        ) : (
                                            <Button variant="destructive" onClick={handleStopRecording} className="h-12 px-8 rounded-full animate-pulse">
                                                {t('chat.scheduling.stopRecording')}
                                            </Button>
                                        )
                                    ) : (
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2 text-green-600 font-medium bg-green-50 px-4 py-2 rounded-full border border-green-100">
                                                <FaCheck /> {t('chat.scheduling.audioRecorded')}
                                            </div>
                                            <Button variant="ghost" onClick={() => setAudioGravado(null)} className="text-red-500">
                                                {t('common.buttons.delete')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {useTemplate && (
                                <div className="space-y-4 pt-4 border-t border-gray-100">
                                    <Select
                                        value={templateSelecionado?.templateId || templateSelecionado?._id}
                                        onValueChange={handleSelecionarTemplate}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder={t('chat.scheduling.selectTemplate')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {templates.map((t) => (
                                                <SelectItem key={t.templateId || t._id} value={t.templateId || t._id}>
                                                    {t.name} ({t.status})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {templateSelecionado && (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                            <div className="p-3 bg-gray-50 rounded border text-sm text-gray-600 whitespace-pre-wrap">
                                                {templateSelecionado.conteudo}
                                            </div>
                                            {Object.keys(templateVariaveis).length > 0 && (
                                                <div className="space-y-2">
                                                    <Label className="text-xs uppercase text-gray-500">{t('chat.scheduling.variables')}</Label>
                                                    {Object.keys(templateVariaveis).map((key) => (
                                                        <Input
                                                            key={key}
                                                            placeholder={t('chat.scheduling.variableValue', { key: `{{${key}}}` })}
                                                            value={templateVariaveis[key]}
                                                            onChange={(e) => setTemplateVariaveis(prev => ({ ...prev, [key]: e.target.value }))}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {activeStep === 3 && (
                    <div className="space-y-6">
                        <Label className={MENSAGENS_AGENDADAS_DESIGN.dialog.label}>{t('chat.scheduling.sendDateTime')}</Label>
                        <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-xl space-y-4">
                            <div>
                                <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">{t('chat.scheduling.date')}</span>
                                <Input type="date" value={dataAgendamento} onChange={(e) => setDataAgendamento(e.target.value)} className="bg-white border-indigo-200" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">{t('chat.scheduling.hour')}</span>
                                    <Select value={hora} onValueChange={setHora}>
                                        <SelectTrigger className="bg-white border-indigo-200">
                                            <SelectValue placeholder="HH" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                                                <SelectItem key={h} value={h.toString().padStart(2, '0')}>
                                                    {h.toString().padStart(2, '0')}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-indigo-800 uppercase mb-1 block">{t('chat.scheduling.minute')}</span>
                                    <Select value={minuto} onValueChange={setMinuto}>
                                        <SelectTrigger className="bg-white border-indigo-200">
                                            <SelectValue placeholder="MM" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {['00', '10', '20', '30', '40', '50'].map((m) => (
                                                <SelectItem key={m} value={m}>
                                                    {m}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-yellow-800 text-sm">
                            <Iconify icon="mdi:information-outline" width={20} className="shrink-0 mt-0.5" />
                            <div>
                                <strong className="block mb-1">{t('chat.scheduling.checkDetails')}</strong> {t('chat.scheduling.sendingTo')} <strong>{numero}</strong> {t('chat.scheduling.onDate')} <strong>{fDateTimeSuffix(`${dataAgendamento}T${hora || '00'}:${minuto || '00'}:00`)}</strong>.
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className={MENSAGENS_AGENDADAS_DESIGN.dialog.footer}>
                <Button variant="outline" onClick={activeStep === 1 ? onClose : handleBack}>
                    {activeStep === 1 ? t('common.buttons.cancel') : t('common.buttons.back')}
                </Button>
                <Button
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] min-w-[120px]"
                    onClick={activeStep === 3 ? onSubmit : handleNext}
                    disabled={loadingSubmit}
                >
                    {loadingSubmit ? t('chat.scheduling.saving') : activeStep === 3 ? t('chat.scheduling.scheduleSend') : `${t('common.buttons.next')} →`}
                </Button>
            </div>
        </div>
    );
}

// Exported Wrapper
export default function DrawerAgendamentos({ typeButton = 'button', numero = '', isEdit = false, source = 'default' }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                {typeButton === 'icon' ? (
                    <Tooltip title={t('chat.scheduling.title')}>
                        <Button variant="outline" size="icon" className="w-7 h-7 bg-white border-gray-200 hover:bg-gray-50">
                            <Iconify icon="mdi:calendar-clock" />
                        </Button>
                    </Tooltip>
                ) : (
                    <Button>{t('chat.scheduling.title')}</Button>
                )}
            </SheetTrigger>
            {/* Using w-[95vw] to take most of the screen like a proper large drawer/sheet. sm:max-w-[700px] constrains it on very large screens for form only. */}
            <SheetContent className="w-[95vw] sm:max-w-[700px] p-0 border-l-0" side="right">
                {open && <DrawerAgendamentosContent numeroDefault={numero} onClose={() => setOpen(false)} />}
            </SheetContent>
        </Sheet>
    );
}